import { useRouterState } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useOpenSearch } from "~/components/ops/command-palette";
import { Separator } from "~/components/ui/separator";
import { SidebarTrigger } from "~/components/ui/sidebar";
import { ThemeToggle } from "~/components/theme-toggle";
import { useOpsStream } from "~/lib/live";

const TITLES: { match: (p: string) => boolean; title: string }[] = [
  { match: (p) => p === "/ops", title: "Today" },
  { match: (p) => p.startsWith("/ops/parcels"), title: "Parcels" },
  { match: (p) => p.startsWith("/ops/transactions"), title: "Transactions" },
  { match: (p) => p.startsWith("/ops/messages"), title: "Messages" },
  { match: (p) => p.startsWith("/ops/addresses"), title: "Shipping addresses" },
  { match: (p) => p.startsWith("/ops/rates"), title: "Freighter rates" },
  { match: (p) => p.startsWith("/ops/users"), title: "Users & roles" },
  { match: (p) => p.startsWith("/ops/account"), title: "Account" },
];

const SHORTCUT = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.userAgent) ? "⌘K" : "Ctrl K";

export function AppHeader() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const status = useOpsStream();
  const openSearch = useOpenSearch();
  const title = TITLES.find((t) => t.match(path))?.title ?? "Ops";

  return (
    <header className="border-hairline bg-surface/80 sticky top-0 z-20 flex h-14 shrink-0 items-center gap-2 border-b px-4 backdrop-blur-md">
      <SidebarTrigger className="-ml-1" />
      <Separator orientation="vertical" className="mr-1 !h-4" />
      <span className="text-[15px] font-semibold tracking-[-0.01em]">{title}</span>
      <div className="ml-auto flex items-center gap-2">
        <button
          type="button"
          onClick={openSearch}
          className="border-hairline bg-surface text-text-faint hover:border-foreground/25 hidden h-8 w-72 items-center gap-2 rounded-md border px-2.5 text-left text-[13px] transition-colors focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:outline-none md:flex"
        >
          <Search className="size-3.5 shrink-0" strokeWidth={1.75} />
          <span className="min-w-0 flex-1 truncate">Find a customer or parcel</span>
          <kbd className="border-hairline rounded border px-1.5 py-0.5 font-sans text-[11px]">{SHORTCUT}</kbd>
        </button>
        <button
          type="button"
          onClick={openSearch}
          aria-label="Find a customer or parcel"
          className="text-muted-foreground hover:bg-accent grid size-10 place-items-center rounded-md transition-colors md:hidden"
        >
          <Search className="size-5" strokeWidth={1.75} />
        </button>
        <span className="text-text-faint hidden items-center gap-1.5 text-[11px] sm:flex">
          <span className={`size-1.5 rounded-full ${status === "open" ? "bg-success" : "bg-text-faint"}`} />
          {status === "open" ? "Live" : "Offline"}
        </span>
        <ThemeToggle />
      </div>
    </header>
  );
}
